import React, {FC} from 'react';
import styled from 'styled-components';

import {Plural, Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {AsyncStatusesEnum} from '../../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {LinkedConversationStatusesEnum} from '../../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {DataEventActionsEnum} from '../../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {LinkedConversationsDataEventObject} from '../../../../../../libs/shared/core/src/models/tracking/objectNames/linkedConversationsDataEventObject';
import {useDataEventTracking} from '../../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Dropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {useLinkNavigation} from '../../../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {Anchor} from '../../../../../../libs/shared/ui/src/components/reposition/repositionHelpers';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {
  ConversationSummaryLinkedConversationsContainer,
  ConversationSummaryLinkedConversationsContainerPublicProps,
} from '../../conversation/conversationSummary/conversationSummaryLinkedConversationsContainer';

/*
 * Props.
 */

export interface HoverLinkedConversationsPreviewerUnfurlProps
  extends ConversationSummaryLinkedConversationsContainerPublicProps {
  shouldShowPreview: boolean;
  anchor: Anchor;
  originalConversation?: ConversationModel;
  conversation: ConversationModel;
}

/*
 * Style.
 */

const StyledDropdown = styled(Dropdown)`
  border-radius: 12px;
`;

const StyledDropDownHeaderContainerDiv = styled.div`
  display: grid;
  align-items: center;
  padding-top: 13px;
  padding-bottom: 5px;
  grid-template-areas: '. icon title button .';
  grid-template-columns: 12px 24px 1fr auto 5px;
  gap: 4px;
`;

const StyledGridIcon = styled(Icon)`
  grid-area: icon;
`;

const StyledTitleDiv = styled.div`
  grid-area: title;
  color: ${(p) => p.theme.greys.shade90};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.semibold};
`;

const StyledDropdownContentContainerDiv = styled.div`
  overflow: auto;
  padding: 0 16px 12px;
`;

const StyledTitleButton = styled(Button)`
  grid-area: button;
`;

/*
 * Component.
 */

export const HoverLinkedConversationsPreviewerUnfurl: FC<HoverLinkedConversationsPreviewerUnfurlProps> = (
  props,
) => {
  const {shouldShowPreview, anchor} = props;
  const theme = useUserTheme();
  const {navigateToLink} = useLinkNavigation();
  const {trackDataEvent} = useDataEventTracking();

  if (!shouldShowPreview) {
    return null;
  }

  const onLinkedConversationViewAllClick = () => {
    const {conversation, originalConversation} = props;
    const selectedConversation = originalConversation ?? conversation;
    if (!selectedConversation.allLinkedConversationsUrl) {
      return;
    }

    navigateToLink(selectedConversation.allLinkedConversationsUrl);
    props.onRequestClose();

    trackDataEvent(DataEventActionsEnum.CLICK, LinkedConversationsDataEventObject.VIEW_ALL, {
      sourceType: selectedConversation.linkStatus,
      sourceConversationId: selectedConversation.id,
      hover: true,
    });
  };

  const renderTitle = () => {
    if (props.linkStatus === LinkedConversationStatusesEnum.LINKED) {
      return <Trans id="hover-linked-conversation-title-original">Original conversation</Trans>;
    }
    return (
      <Plural
        id="hover-linked-conversation-title-linked"
        value={props.conversation.numLinkedConversations}
        one="1 linked conversation"
        other="# linked conversations"
      />
    );
  };

  return (
    <StyledDropdown
      width={300}
      maxHeight={275}
      anchor={anchor}
      placement="bottom-start"
      isExclusive={false}
      isInline
    >
      <StyledDropDownHeaderContainerDiv>
        <StyledGridIcon name="linkedConversationsFilled" color={theme.greys.shade80} />
        <StyledTitleDiv>{renderTitle()}</StyledTitleDiv>
        {props.linkedConversationActivities.status === AsyncStatusesEnum.LOADED &&
          props.linkedConversationActivities.data.length > 0 && (
            <StyledTitleButton
              onClick={onLinkedConversationViewAllClick}
              variant={ButtonVariantsEnum.LINK}
              size={VisualSizesEnum.SMALL}
            >
              <Trans id="linked-converastions-view-all">View all</Trans>
            </StyledTitleButton>
          )}
      </StyledDropDownHeaderContainerDiv>
      <StyledDropdownContentContainerDiv>
        <ConversationSummaryLinkedConversationsContainer
          panelId={props.panelId}
          linkedConversationActivities={props.linkedConversationActivities}
          onLinkedConversationsRetryClick={props.onLinkedConversationsRetryClick}
          onRequestClose={props.onRequestClose}
          linkStatus={props.linkStatus}
          isCompact
        />
      </StyledDropdownContentContainerDiv>
    </StyledDropdown>
  );
};
